import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import * as styles from './table.module.scss';

import { SortingOptions, SortState } from './Sorting/types';
import TableColumnHeader from './TableColumnHeader';
import { ColumnConfig } from './type';

interface TableHeadProps {
  columns: ColumnConfig[];
  isHeaderSticky?: boolean;
  isFirstColumnSticky?: boolean;
  defaultSortingOptions?: SortingOptions;
  sortingOptions?: SortingOptions;
  setSortingOptions?: (options: SortingOptions) => void;
  onRender?: (ref: any) => void;
}

const TableHead: React.FC<TableHeadProps> = (props) => {
  const {
    columns,
    isHeaderSticky,
    isFirstColumnSticky,
    sortingOptions,
    setSortingOptions,
    defaultSortingOptions,
    onRender,
  } = props;

  const getDefaultSortingOptions = () => {
    const opts = sortingOptions ?? defaultSortingOptions;
    if (!opts) return;
    const { headerKey, sortDir: sortDirection, sortKey, label = '' } = opts;
    return { headerKey, sortDirection, sortKey, label };
  };


  const [selectedSorting, setSelectedSorting] = useState<SortState | undefined>(
    getDefaultSortingOptions()
  );

  const trRef = useRef(null);

  useEffect(() => {
    if (trRef.current && onRender) {
      onRender(trRef.current);
    }
  }, [onRender]);

  useEffect(() => {
    if (sortingOptions) {
      const { headerKey, sortKey, sortDir, label } = sortingOptions;
      setSelectedSorting({
        headerKey,
        sortDirection: sortDir,
        sortKey,
        label: label || '',
      });
    }
  }, [sortingOptions]);

  const updateSort = (sortingSelection: SortState) => {
    const { headerKey, sortKey, sortDirection, label } = sortingSelection;
    const sortColumnConfig = columns.find((c) => c.headerKey === headerKey);
    if (!sortColumnConfig) {
      return;
    }

    if (setSortingOptions) {
      setSortingOptions({
        headerKey,
        sortKey,
        sortDir: sortDirection,
        label,
      });
    }

    setSelectedSorting({
      headerKey,
      sortDirection,
      sortKey,
      label,
    });
  };

  return (
    <thead>
      <tr className={styles.Table__HeaderRow} ref={trRef}>
        {columns
          .filter((c) => !c.isHidden)
          .map((column: ColumnConfig, indx: number) => {
            const { headerKey, isSortable, sortFields, compact, name, className, defaultSortDirection, tooltip } =
              column;
            return (
              <TableColumnHeader
                name={name}
                headerKey={headerKey}
                className={classnames(className, {
                  [styles.Table__HeaderRow__stickyFirstColumnHeader]: indx === 0 && isFirstColumnSticky,
                })}
                isSticky={isHeaderSticky}
                compact={compact}
                key={headerKey}
                updateSort={(selection) => updateSort(selection)}
                //to fix the issue that might be caused by race condition
                selectedSorting={ selectedSorting?.headerKey !== sortingOptions?.headerKey ? getDefaultSortingOptions(): selectedSorting}
                isSortable={!!isSortable}
                sortFields={sortFields}
                defaultSortDirConfig={defaultSortDirection}
                tooltip={tooltip}
              />
            );
          })}
      </tr>
    </thead>
  );
};

export default TableHead;
